// Navbar.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  MdCalendarToday, 
  MdInsertDriveFile, 
  MdSettings, 
  MdPeople, 
  MdLaptopMac, 
  MdStore,
  MdOutlineInfo,
  MdOutlineLightbulb,
  MdHelpOutline,
  MdPersonOutline,
  MdAddCircleOutline,
  MdLogout,
  MdBusiness,
  MdSwapHoriz,
  MdDashboard,
  MdClose
} from 'react-icons/md';
import { IoEye } from 'react-icons/io5';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [showHelpDropdown, setShowHelpDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showLabDropdown, setShowLabDropdown] = useState(false);
  const [adminName, setAdminName] = useState('User');
  const [userRole, setUserRole] = useState('');
  const [labName, setLabName] = useState('Select a Lab');
  const [labId, setLabId] = useState('');
  const [labNames, setLabNames] = useState([]);
  const [labIdMap, setLabIdMap] = useState({});
  const [loadingLabs, setLoadingLabs] = useState(false);
  const [showAllLabs, setShowAllLabs] = useState(false);
  const [hasPreselectedLab, setHasPreselectedLab] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if current page is dashboard
  const isDashboardPage = location.pathname === '/dashboard';
  
  // Check if user is staff
  const isStaff = userRole === 'staff' || userRole === 'Staff';

  // Refs for dropdown containers
  const helpDropdownRef = useRef(null);
  const userDropdownRef = useRef(null);
  const labDropdownRef = useRef(null);
  const helpButtonRef = useRef(null);
  const userButtonRef = useRef(null);
  const labButtonRef = useRef(null);

  // Clear lab selection when on dashboard page - WITHOUT RELOAD
  useEffect(() => {
    if (isDashboardPage) {
      const shouldClearLab = localStorage.getItem('clearLabOnDashboard') === 'true';
      if (shouldClearLab) {
        // Clear lab without reload
        clearLabWithoutReload();
        localStorage.removeItem('clearLabOnDashboard');
      }
    }
  }, [isDashboardPage]);

  // Function to clear lab without reload
  const clearLabWithoutReload = () => {
    // Remove lab-related items from localStorage
    localStorage.removeItem("userLabId");
    localStorage.removeItem("labName");
    localStorage.removeItem("labData");
    
    // Update state directly without reload
    setLabName('Select a Lab');
    setLabId('');
    setHasPreselectedLab(false);
    setShowAllLabs(false);
    setShowLabDropdown(false);
  };

  // Fetch user role and data on component mount
  useEffect(() => {
    const initializeNavbar = async () => {
      fetchUserData();
      // Only fetch labs for admin users
      if (!isStaff) {
        await fetchAdminLabs();
      }
    };
    initializeNavbar();
  }, []);

  // Auto-select lab when labNames and labIdMap are loaded
  useEffect(() => {
    if (labNames.length > 0 && Object.keys(labIdMap).length > 0) {
      const storedLabId = localStorage.getItem('userLabId');
      const storedLabName = localStorage.getItem('labName');

      // Don't auto-select if on dashboard page with clear flag
      const shouldSkipAutoSelect = isDashboardPage && localStorage.getItem('clearLabOnDashboard') === 'true';
      
      if (!shouldSkipAutoSelect && storedLabId) {
        // Find the lab name by ID from the map
        let foundLabName = null;
        
        // Check if stored lab name matches
        if (storedLabName && labNames.includes(storedLabName)) {
          foundLabName = storedLabName;
        } else {
          // Find lab name by ID
          for (const [name, id] of Object.entries(labIdMap)) {
            if (id === storedLabId) {
              foundLabName = name;
              break;
            }
          }
        }

        // If lab found, auto-select it
        if (foundLabName) {
          setLabName(foundLabName);
          setLabId(storedLabId);
          setHasPreselectedLab(true);
          
          // Ensure localStorage is consistent
          localStorage.setItem('userLabId', storedLabId);
          localStorage.setItem('labName', foundLabName);
          localStorage.setItem('labData', JSON.stringify({
            id: storedLabId,
            name: foundLabName
          }));
        } else {
          // Lab ID exists but not found in available labs, reset
          clearLabWithoutReload();
        }
      }
    }
  }, [labNames, labIdMap, isDashboardPage]);

  useEffect(() => {
    const handleLabChanged = () => {
      const storedLabName = localStorage.getItem('labName');
      const storedLabId = localStorage.getItem('userLabId');

      if (storedLabName && storedLabId) {
        setLabName(storedLabName);
        setLabId(storedLabId);
        setHasPreselectedLab(true);
      } else {
        setLabName('Select a Lab');
        setLabId('');
        setHasPreselectedLab(false);
      }
    };

    window.addEventListener('labChanged', handleLabChanged);

    return () => {
      window.removeEventListener('labChanged', handleLabChanged);
    };
  }, []);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close Help dropdown if clicked outside
      if (showHelpDropdown && 
          helpDropdownRef.current && 
          !helpDropdownRef.current.contains(event.target) &&
          helpButtonRef.current && 
          !helpButtonRef.current.contains(event.target)) {
        setShowHelpDropdown(false);
      }

      // Close User dropdown if clicked outside
      if (showUserDropdown && 
          userDropdownRef.current && 
          !userDropdownRef.current.contains(event.target) &&
          userButtonRef.current && 
          !userButtonRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }

      // Close Lab dropdown and reset showAllLabs if clicked outside
      if (showLabDropdown && 
          labDropdownRef.current && 
          !labDropdownRef.current.contains(event.target) &&
          labButtonRef.current && 
          !labButtonRef.current.contains(event.target)) {
        setShowLabDropdown(false);
        setShowAllLabs(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHelpDropdown, showUserDropdown, showLabDropdown]);

  const fetchAdminLabs = async () => {
    try {
      setLoadingLabs(true);

      const token = localStorage.getItem('accessToken') || localStorage.getItem('access_token') || localStorage.getItem('token');

      if (!token) {
        setLoadingLabs(false);
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'https://hms.backend.hiragroupcloud.com'}/api/auth/admins`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success && response.data.users) {
        const labMap = {};
        const uniqueLabs = [];

        response.data.users.forEach((user) => {
          const labNameValue = user.labName || user.companyName || user.organization;
          const labIdValue = user.labId || user._id;

          if (labNameValue && labNameValue.trim() && !labMap[labNameValue]) {
            labMap[labNameValue] = labIdValue;
            uniqueLabs.push(labNameValue);
          }
        });

        setLabNames(uniqueLabs);
        setLabIdMap(labMap);

        // Check if there's a stored lab to auto-select
        const storedLabId = localStorage.getItem('userLabId');
        const storedLabName = localStorage.getItem('labName');
        
        if (storedLabId && storedLabName) {
          // Find if the stored lab exists in the fetched labs
          let labExists = false;
          
          if (uniqueLabs.includes(storedLabName)) {
            labExists = true;
          } else {
            // Check by ID
            for (const [name, id] of Object.entries(labMap)) {
              if (id === storedLabId) {
                labExists = true;
                break;
              }
            }
          }
          
          if (!labExists) {
            // Stored lab doesn't exist in available labs, reset
            clearLabWithoutReload();
          }
        }
      } else {
        setLabNames([]);
        setLabIdMap({});
        clearLabWithoutReload();
      }
    } catch (err) {
      console.error("Error fetching admin labs:", err);
      setLabNames([]);
      setLabIdMap({});
      clearLabWithoutReload();
    } finally {
      setLoadingLabs(false);
    }
  };

  const fetchUserData = () => {
    try {
      let userData = null;
      let userDataString = localStorage.getItem('userData');

      if (userDataString) {
        userData = JSON.parse(userDataString);
      } else {
        const userString = localStorage.getItem('user');
        if (userString) {
          userData = JSON.parse(userString);
        }
      }

      if (userData) {
        // Set user role
        const role = userData.role || localStorage.getItem('userRole') || 'staff';
        setUserRole(role);

        // Set admin name
        let userName = userData.username || userData.first_Name || userData.email || 'User';

        if (userData.first_Name && userData.last_Name) {
          userName = `${userData.first_Name} ${userData.last_Name}`;
        } else if (userData.username) {
          userName = userData.username
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        } else if (userData.email) {
          const emailName = userData.email.split('@')[0];
          userName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
        }

        setAdminName(userName);
      }

      // Check if lab is already selected
      const storedLabId = localStorage.getItem('userLabId');
      const storedLabName = localStorage.getItem('labName');
      
      if (storedLabId && storedLabName && storedLabName !== 'Select a Lab') {
        setHasPreselectedLab(true);
        setLabName(storedLabName);
        setLabId(storedLabId);
      }
    } catch (error) {
      console.error('Error fetching data from localStorage:', error);
      setAdminName('User');
    }
  };

  const handleLabSelect = (selectedLabName, skipReload = false) => {
    const selectedLabId = labIdMap[selectedLabName];

    if (selectedLabId) {
      localStorage.setItem("userLabId", selectedLabId);
      localStorage.setItem("labName", selectedLabName);
      // Remove the clear flag when selecting a lab
      localStorage.removeItem('clearLabOnDashboard');

      localStorage.setItem("labData", JSON.stringify({
        id: selectedLabId,
        name: selectedLabName
      }));

      setLabName(selectedLabName);
      setLabId(selectedLabId);
      setHasPreselectedLab(true);
      setShowAllLabs(false);
      setShowLabDropdown(false);

      if (!skipReload) {
        window.location.reload();
      }
    }
  };

  const handleRemoveLabSelection = () => {
    clearLabWithoutReload();
    navigate('/dashboard');
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  const toggleHelpDropdown = () => {
    setShowUserDropdown(false);
    setShowLabDropdown(false);
    setShowHelpDropdown(!showHelpDropdown);
  };

  const toggleUserDropdown = () => {
    setShowHelpDropdown(false);
    setShowLabDropdown(false);
    setShowUserDropdown(!showUserDropdown);
  };

  const toggleLabDropdown = () => {
    setShowHelpDropdown(false);
    setShowUserDropdown(false);
    setShowLabDropdown(!showLabDropdown);
    if (!showLabDropdown) {
      setShowAllLabs(false);
    }
  };

  // Handle Dashboard navigation
  const handleDashboardClick = () => {
    // Set flag to clear lab on dashboard
    localStorage.setItem('clearLabOnDashboard', 'true');
    // Clear lab immediately without reload
    clearLabWithoutReload();
    // Navigate to dashboard
    navigate('/dashboard');
  };

  const handleItemClick = (item) => {
    setShowHelpDropdown(false);
    setShowUserDropdown(false);
    setShowLabDropdown(false);
    setShowAllLabs(false);

    if (item.onClick) {
      item.onClick();
    }
  };

  const DropdownItem = ({ item, onClick, isLogout = false, showBorder = true }) => {
    const IconComponent = item.icon;
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '10px 15px',
          borderBottom: showBorder ? '1px solid #f0f0f0' : 'none',
          cursor: 'pointer',
          color: isLogout ? '#d32f2f' : '#333',
          fontWeight: isLogout ? '500' : 'normal',
          transition: 'all 0.2s ease',
          backgroundColor: 'white'
        }}
        onClick={() => onClick(item)}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f5f5f5';
          e.currentTarget.style.transform = 'translateX(5px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'white';
          e.currentTarget.style.transform = 'translateX(0)';
        }}
      >
        {IconComponent && <IconComponent style={{ marginRight: '10px', fontSize: '20px' }} />}
        <span style={{ flex: 1 }}>{item.label}</span>
        {item.badge && (
          <span style={{
            backgroundColor: '#e6f7ff',
            color: '#004aad',
            fontSize: '11px',
            padding: '2px 6px',
            borderRadius: '10px',
            fontWeight: '600'
          }}>
            {item.badge}
          </span>
        )}
      </div>
    );
  };

  const helpItems = [
    { id: 1, label: 'Support', icon: MdHelpOutline },
    { id: 2, label: 'Manual', icon: MdOutlineInfo },
    { id: 3, label: 'Keyboard Shortcuts', icon: MdOutlineInfo },
    { id: 4, label: 'New Idea', icon: MdOutlineLightbulb }
  ];

  // Admin user items
  const adminUserItems = [
    { id: 1, label: 'My Account', icon: MdPersonOutline },
    { id: 2, label: '+ Add Account', icon: MdAddCircleOutline },
    {
      id: 3,
      label: 'Logout',
      icon: MdLogout,
      onClick: handleLogout
    }
  ];

  // Staff user items - Only Logout
  const staffUserItems = [
    {
      id: 1,
      label: 'Logout',
      icon: MdLogout,
      onClick: handleLogout
    }
  ];

  // Choose user items based on role
  const userItems = isStaff ? staffUserItems : adminUserItems;

  const labItems = [
    {
      id: 7,
      label: 'Switch Lab',
      icon: MdSwapHoriz,
      onClick: () => setShowAllLabs(true)
    },
    { id: 1, label: 'Calendar', icon: MdCalendarToday, badge: '12' },
    { id: 2, label: 'Files', icon: MdInsertDriveFile },
    { id: 3, label: 'Lab Settings', icon: MdSettings },
    { id: 4, label: 'Users', icon: MdPeople },
    { id: 5, label: 'Lab Details', icon: MdLaptopMac },
    { id: 6, label: 'Store', icon: MdStore }
  ];

  return (
    <div
      style={{
        height: '70px',
        width: '100%',
        backgroundColor: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 30px',
        boxShadow: '0 2px 15px rgba(0, 75, 173, 0.15)',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        position: 'relative',
        zIndex: 1000,
        boxSizing: 'border-box'
      }}
    >
      {/* Left Side - Logo */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        flexShrink: 0,
        marginLeft: '60px'
      }}>
        <img
          src={require('../image.png')}
          alt="HMS Logo"
          style={{
            height: '50px',
            width: 'auto',
            objectFit: 'contain',
            display: 'block'
          }}
        />
      </div>

      {/* Right Side - Links */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        fontSize: '14px',
        flexShrink: 0,
        marginRight: '20px'
      }}>

        {/* Dashboard Button - Only for admin users and not on dashboard page */}
        {!isStaff && !isDashboardPage && (
          <span
            onClick={handleDashboardClick}
            style={{
              color: '#004aad',
              fontWeight: '500',
              cursor: 'pointer',
              padding: '6px 12px',
              borderRadius: '6px',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              fontSize: '13px',
              border: '1px solid transparent'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f0f5ff';
              e.currentTarget.style.border = '1px solid #d0e0ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.border = '1px solid transparent';
            }}
          >
            <MdDashboard style={{ fontSize: '16px' }} />
            Dashboard
          </span>
        )}

        {/* Help Dropdown - Only for admin users */}
        {!isStaff && (
          <div style={{ position: 'relative' }}>
            <span
              ref={helpButtonRef}
              style={{
                color: '#004aad',
                fontWeight: '600',
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px'
              }}
              onClick={(e) => {
                e.stopPropagation();
                toggleHelpDropdown();
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f5ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <MdHelpOutline style={{ fontSize: '18px' }} />
              Help
            </span>

            {showHelpDropdown && (
              <div
                ref={helpDropdownRef}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  backgroundColor: 'white',
                  border: '1px solid #e0e7ff',
                  borderRadius: '8px',
                  boxShadow: '0 15px 40px rgba(0,0,0,0.15)',
                  minWidth: '180px',
                  zIndex: 1001,
                  marginTop: '8px',
                  overflow: 'hidden'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {helpItems.map(item => (
                  <DropdownItem
                    key={item.id}
                    item={item}
                    onClick={handleItemClick}
                    showBorder={false}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* User Dropdown */}
        <div style={{ position: 'relative' }}>
          <span
            ref={userButtonRef}
            style={{
              color: '#004aad',
              cursor: 'pointer',
              padding: '8px 12px',
              borderRadius: '6px',
              transition: 'all 0.2s ease',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '14px'
            }}
            onClick={(e) => {
              e.stopPropagation();
              toggleUserDropdown();
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f0f5ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <MdPersonOutline style={{ fontSize: '18px' }} />
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {adminName}
              {/* Staff Badge */}
              {isStaff && (
                <span style={{
                  backgroundColor: '#fef3c7',
                  color: '#92400e',
                  fontSize: '10px',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  fontWeight: '600',
                  letterSpacing: '0.5px'
                }}>
                  Staff
                </span>
              )}
            </span>
          </span>

          {showUserDropdown && (
            <div
              ref={userDropdownRef}
              style={{
                position: 'absolute',
                top: '100%',
                right: '0',
                backgroundColor: 'white',
                border: '1px solid #e0e7ff',
                borderRadius: '8px',
                boxShadow: '0 15px 40px rgba(0,0,0,0.15)',
                minWidth: isStaff ? '180px' : '200px',
                zIndex: 1001,
                marginTop: '8px',
                overflow: 'hidden'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Staff user info header */}
              {isStaff && (
                <div style={{
                  padding: '12px 15px',
                  backgroundColor: '#fefce8',
                  borderBottom: '1px solid #fde68a'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '4px'
                  }}>
                    <MdPersonOutline style={{ fontSize: '18px', color: '#92400e' }} />
                    <span style={{ fontWeight: '600', color: '#92400e', fontSize: '13px' }}>
                      {adminName}
                    </span>
                  </div>
                  <span style={{
                    display: 'inline-block',
                    backgroundColor: '#fef3c7',
                    color: '#92400e',
                    fontSize: '10px',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontWeight: '600'
                  }}>
                    Staff Account
                  </span>
                </div>
              )}

              {userItems.map((item, index) => (
                <DropdownItem
                  key={item.id}
                  item={item}
                  onClick={handleItemClick}
                  isLogout={item.label === 'Logout'}
                  showBorder={index < userItems.length - 1}
                />
              ))}
            </div>
          )}
        </div>

        {/* Laboratory Dropdown - Only for admin users */}
        {!isStaff && (
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span
                ref={labButtonRef}
                style={{
                  color: labName === 'Select a Lab' ? '#666' : '#004aad',
                  fontWeight: '600',
                  cursor: 'pointer',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  backgroundColor: showLabDropdown ? '#f0f5ff' : 'transparent',
                  fontSize: '14px',
                  maxWidth: '200px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontStyle: labName === 'Select a Lab' ? 'italic' : 'normal',
                  border: '1px solid transparent'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleLabDropdown();
                }}
                onMouseEnter={(e) => {
                  if (!showLabDropdown) {
                    e.currentTarget.style.backgroundColor = '#f0f5ff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!showLabDropdown) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <MdBusiness style={{ fontSize: '18px' }} />
                <span style={{
                  maxWidth: '140px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {labName}
                </span>
              </span>

              {/* Remove Lab Selection Button - Only show when lab is selected */}
              {hasPreselectedLab && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveLabSelection();
                  }}
                  title="Clear selected lab"
                  style={{
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#fff3f3',
                    border: '1px solid #ffcdd2',
                    color: '#d32f2f',
                    fontSize: '12px',
                    transition: 'all 0.2s ease',
                    fontWeight: '500'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ffebee';
                    e.currentTarget.style.borderColor = '#ef9a9a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff3f3';
                    e.currentTarget.style.borderColor = '#ffcdd2';
                  }}
                >
                  <MdClose style={{ fontSize: '14px', marginRight: '4px' }} />
                  
                </span>
              )}
            </div>

            {showLabDropdown && (
              <div
                ref={labDropdownRef}
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: '0',
                  backgroundColor: 'white',
                  border: '1px solid #e0e7ff',
                  borderRadius: '10px',
                  boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
                  minWidth: '320px',
                  zIndex: 1001,
                  marginTop: '8px',
                  overflow: 'hidden'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Current Lab Section */}
                <div style={{
                  padding: '15px 20px',
                  backgroundColor: '#f8faff',
                  borderBottom: '1px solid #e0e7ff'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: labName === 'Select a Lab' ? '#999' : '#004aad',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}>
                      {labName === 'Select a Lab' ? '?' : (labName.charAt(0) || 'L')}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <p style={{
                        margin: '0',
                        color: labName === 'Select a Lab' ? '#666' : '#004aad',
                        fontWeight: '700',
                        fontSize: '16px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontStyle: labName === 'Select a Lab' ? 'italic' : 'normal'
                      }}>
                        {labName}
                      </p>
                      <p style={{
                        margin: '4px 0 0',
                        color: '#666',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {labName === 'Select a Lab' ? 'Please select a laboratory' : `Lab ID: ${labId || 'N/A'}`}
                      </p>
                    </div>
                  </div>
                  {/* Clear Selection Button in Dropdown */}
                  {hasPreselectedLab && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveLabSelection();
                        setShowLabDropdown(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '8px',
                        marginTop: '10px',
                        backgroundColor: '#fff3f3',
                        color: '#d32f2f',
                        border: '1px solid #ffcdd2',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#ffebee';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fff3f3';
                      }}
                    >
                      <MdClose style={{ fontSize: '14px' }} />
                      Clear Selected Lab
                    </button>
                  )}
                </div>

                {/* Switch Lab Section */}
                <div style={{
                  padding: '15px 20px',
                  borderBottom: '1px solid #f0f0f0'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px'
                  }}>
                    <p style={{
                      margin: '0',
                      color: '#666',
                      fontSize: '12px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {labName === 'Select a Lab' ? 'Select Laboratory' : 'Switch Laboratory'}
                    </p>
                    <span style={{
                      fontSize: '11px',
                      color: '#888',
                      backgroundColor: '#f0f0f0',
                      padding: '2px 8px',
                      borderRadius: '10px'
                    }}>
                      {labNames.length} options
                    </span>
                  </div>

                  {showAllLabs ? (
                    <div style={{
                      maxHeight: '280px',
                      overflowY: 'auto',
                      marginTop: '10px'
                    }}>
                      {loadingLabs ? (
                        <div style={{
                          padding: '20px',
                          textAlign: 'center',
                          color: '#666'
                        }}>
                          <div style={{
                            width: '30px',
                            height: '30px',
                            border: '3px solid #f3f3f3',
                            borderTop: '3px solid #004aad',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 10px'
                          }} />
                          Loading labs...
                        </div>
                      ) : (
                        <>
                          {labNames.length === 0 && !loadingLabs && (
                            <div style={{
                              padding: '20px',
                              textAlign: 'center',
                              color: '#666',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '8px',
                              fontSize: '14px'
                            }}>
                              No labs available
                            </div>
                          )}

                          {labNames.map((labNameItem, index) => (
                            <div
                              key={index}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '10px',
                                marginBottom: '8px',
                                borderRadius: '8px',
                                backgroundColor: labName === labNameItem ? '#e6f0ff' : '#f8f9fa',
                                border: labName === labNameItem ? '1px solid #004aad' : '1px solid #e0e7ff',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                              }}
                              onMouseEnter={(e) => {
                                if (labName !== labNameItem) {
                                  e.currentTarget.style.backgroundColor = '#f0f5ff';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (labName !== labNameItem) {
                                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                                }
                              }}
                              onClick={() => handleLabSelect(labNameItem, false)}
                            >
                              <div style={{
                                width: '32px',
                                height: '32px',
                                backgroundColor: labName === labNameItem ? '#004aad' : '#d0e0ff',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: '12px',
                                color: labName === labNameItem ? 'white' : '#004aad',
                                fontWeight: 'bold',
                                fontSize: '14px',
                                flexShrink: 0
                              }}>
                                {labNameItem.charAt(0)}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{
                                  margin: '0',
                                  color: '#333',
                                  fontWeight: '600',
                                  fontSize: '14px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {labNameItem}
                                </p>
                                <p style={{
                                  margin: '2px 0 0',
                                  color: '#666',
                                  fontSize: '11px',
                                }}>
                                  ID: {labIdMap[labNameItem] || 'N/A'}
                                </p>
                              </div>
                              {labName === labNameItem ? (
                                <div style={{
                                  width: '8px',
                                  height: '8px',
                                  backgroundColor: '#00c853',
                                  borderRadius: '50%'
                                }} />
                              ) : (
                                <IoEye style={{
                                  fontSize: '18px',
                                  color: '#888',
                                }} />
                              )}
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => setShowAllLabs(true)}
                        style={{
                          width: '100%',
                          padding: '10px',
                          backgroundColor: '#004aad',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '13px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          transition: 'all 0.2s ease',
                          marginBottom: '15px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#003c8f';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#004aad';
                        }}
                      >
                        <MdSwapHoriz style={{ fontSize: '18px' }} />
                        {labName === 'Select a Lab' ? 'Select a Lab' : 'Switch Lab'}
                      </button>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '10px'
                      }}>
                        {labNames.slice(0, 4).map((labNameItem, index) => (
                          <div
                            key={index}
                            style={{
                              padding: '10px',
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #e0e7ff',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              textAlign: 'center'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#e6f0ff';
                              e.currentTarget.style.transform = 'scale(1.02)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#f8f9fa';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                            onClick={() => handleLabSelect(labNameItem, false)}
                          >
                            <div style={{
                              width: '28px',
                              height: '28px',
                              backgroundColor: '#004aad',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              margin: '0 auto 6px',
                              color: 'white',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}>
                              {labNameItem.charAt(0)}
                            </div>
                            <p style={{
                              margin: '0',
                              color: '#333',
                              fontSize: '11px',
                              fontWeight: '600',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {labNameItem}
                            </p>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Lab Settings Section */}
                {!showAllLabs && (
                  <div style={{
                    padding: '15px 20px',
                    borderBottom: '1px solid #f0f0f0'
                  }}>
                    <p style={{
                      margin: '0 0 10px',
                      color: '#666',
                      fontSize: '12px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Lab Settings
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      {labItems.slice(1, 5).map(item => (
                        <button
                          key={item.id}
                          onClick={() => handleItemClick(item)}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '10px',
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #e0e7ff',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            color: '#333',
                            position: 'relative'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#e6f0ff';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          <item.icon style={{ fontSize: '20px', color: '#004aad', marginBottom: '6px' }} />
                          <span style={{ fontSize: '11px', fontWeight: '600' }}>{item.label}</span>
                          {item.badge && (
                            <span style={{
                              position: 'absolute',
                              top: '6px',
                              right: '6px',
                              backgroundColor: '#ff4444',
                              color: 'white',
                              fontSize: '9px',
                              padding: '1px 4px',
                              borderRadius: '50%',
                              minWidth: '14px',
                              height: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {item.badge}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Footer for All Labs View */}
                {showAllLabs && (
                  <div style={{
                    padding: '12px 20px',
                    backgroundColor: '#f8faff',
                    borderTop: '1px solid #e0e7ff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <button
                      onClick={() => setShowAllLabs(false)}
                      style={{
                        backgroundColor: 'transparent',
                        color: '#666',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f0f0f0';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      ← Back
                    </button>
                    <span style={{
                      fontSize: '11px',
                      color: '#888'
                    }}>
                      Click a lab to select
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Inline CSS */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default Navbar;